import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { KITCHEN, IMG } from "../data/content";
import { SectionLabel } from "./Section";
import { fadeUp, viewport } from "../lib/motion";

const ParallaxImg = ({ src, alt }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="h-full w-full overflow-hidden">
      <motion.img
        style={{ y, scale: 1.15 }}
        src={src}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-cover"
      />
    </div>
  );
};

const MediterraneanKitchen = () => {
  const [breakfast, sandwiches, salads, pastries, brunch] = KITCHEN;

  return (
    <section className="relative py-16 sm:py-32 bg-surface/40" data-testid="kitchen-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <SectionLabel>Mediterranean Kitchen</SectionLabel>
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]"
          >
            Sun-soaked flavors, <span className="text-highlight">served all day.</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="mt-5 text-foreground/70 text-base sm:text-lg leading-relaxed"
          >
            A menu rooted in the Mediterranean — fresh, generous, and made from scratch every morning in our Arlington kitchen.
          </motion.p>
        </div>

        {/* Editorial asymmetric grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[220px] sm:auto-rows-[260px]">
          {/* Breakfast — large feature */}
          <motion.article
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="group relative overflow-hidden rounded-3xl shadow-warm md:col-span-7 md:row-span-2"
            data-testid="kitchen-card-breakfast"
          >
            <ParallaxImg src={breakfast.img} alt={breakfast.name} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <h3 className="font-serif text-3xl sm:text-4xl text-background">{breakfast.name}</h3>
              <p className="mt-2 max-w-md text-background/85 text-sm sm:text-base leading-relaxed">{breakfast.copy}</p>
            </div>
          </motion.article>

          {/* Sandwiches */}
          <motion.article
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="group relative overflow-hidden rounded-3xl shadow-warm md:col-span-5"
            data-testid="kitchen-card-sandwiches"
          >
            <ParallaxImg src={sandwiches.img} alt={sandwiches.name} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 p-6">
              <h3 className="font-serif text-2xl sm:text-3xl text-background">{sandwiches.name}</h3>
              <p className="mt-1.5 text-background/80 text-sm leading-relaxed">{sandwiches.copy}</p>
            </div>
          </motion.article>

          {/* Salads */}
          <motion.article
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="group relative overflow-hidden rounded-3xl shadow-warm md:col-span-5"
            data-testid="kitchen-card-salads"
          >
            <ParallaxImg src={salads.img} alt={salads.name} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 p-6">
              <h3 className="font-serif text-2xl sm:text-3xl text-background">{salads.name}</h3>
              <p className="mt-1.5 text-background/80 text-sm leading-relaxed">{salads.copy}</p>
            </div>
          </motion.article>

          {/* Pastries */}
          <motion.article
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="group relative overflow-hidden rounded-3xl shadow-warm md:col-span-4"
            data-testid="kitchen-card-pastries"
          >
            <ParallaxImg src={pastries.img} alt={pastries.name} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 p-6">
              <h3 className="font-serif text-2xl sm:text-3xl text-background">{pastries.name}</h3>
              <p className="mt-1.5 text-background/80 text-sm leading-relaxed">{pastries.copy}</p>
            </div>
          </motion.article>

          {/* Weekend Brunch */}
          <motion.article
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="group relative overflow-hidden rounded-3xl shadow-warm md:col-span-8"
            data-testid="kitchen-card-weekend-brunch"
          >
            <ParallaxImg src={brunch.img} alt={brunch.name} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 p-6 sm:p-8">
              <h3 className="font-serif text-2xl sm:text-3xl text-background">{brunch.name}</h3>
              <p className="mt-1.5 max-w-md text-background/80 text-sm leading-relaxed">{brunch.copy}</p>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
};

export default MediterraneanKitchen;
